import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const trainingFlow = [
  {
    step: '专家评课',
    desc: '按统一评价表评课，沉淀专家经验',
    icon: 'ri-user-star-line',
    color: 'accent' as const,
    num: '01',
  },
  {
    step: '指标标注',
    desc: '专家意见 + 课堂片段 + 指标标签',
    icon: 'ri-price-tag-3-line',
    color: 'primary' as const,
    num: '02',
  },
  {
    step: '样本训练',
    desc: '模型在样本中学习评价规律',
    icon: 'ri-database-2-line',
    color: 'accent' as const,
    num: '03',
  },
  {
    step: '模型评价',
    desc: '切片 + 多模态 + 指标映射 + 输出结果',
    icon: 'ri-brain-line',
    color: 'primary' as const,
    num: '04',
  },
  {
    step: '人机比对',
    desc: '历史课程验证 + 专家抽检校准',
    icon: 'ri-scales-3-line',
    color: 'accent' as const,
    num: '05',
  },
  {
    step: '系统上线',
    desc: '进入常态化应用与服务',
    icon: 'ri-rocket-line',
    color: 'primary' as const,
    num: '06',
  },
];

export default function P21ExperienceToModel({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 背景光晕 */}
      <div ref={orb1Ref} className="absolute top-20 right-20 w-[500px] h-[500px] rounded-full bg-accent-100/8 blur-[140px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-10 left-10 w-[400px] h-[400px] rounded-full bg-primary-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-12 md:mb-16">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50/80 border border-accent-200/40 text-xs font-body font-semibold text-accent-600 tracking-[0.12em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <i className="ri-route-line text-sm" />
            模型训练路径
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-[3rem] font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从<span className="text-primary-600">经验印象</span>到<span className="text-accent-600">模型训练</span>
          </h2>
          <p className={`text-sm md:text-base text-foreground-500 font-body max-w-xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI的能力来自专家经验的规模化、数据化和模型化
          </p>

          {/* 标题下方装饰线 */}
          <div className={`flex items-center justify-center gap-3 mt-6 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="w-8 h-px bg-accent-300/50" />
            <div className="w-2 h-2 rounded-full bg-accent-400" />
            <div className="w-12 h-px bg-primary-300/40" />
            <div className="w-2 h-2 rounded-full bg-primary-400" />
            <div className="w-8 h-px bg-accent-300/50" />
          </div>
        </div>

        {/* 主流程区域 - 蛇形双行 */}
        <div className={`max-w-6xl mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 上排 3 个 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-5 md:mb-6 relative">
            {trainingFlow.slice(0, 3).map((item, i) => {
              const isAccent = item.color === 'accent';
              return (
                <div key={item.step} className="relative">
                  <StepCard item={item} isAccent={isAccent} delay={300 + i * 120} />
                  {/* 桌面端右侧连接线 */}
                  {i < 2 && (
                    <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 items-center">
                      <div className="w-6 h-px bg-gradient-to-r from-primary-200/40 to-accent-200/40" />
                      <div className="w-6 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-accent-300/60" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 中间连接线（大弧线示意） */}
          <div className="hidden md:flex items-center justify-center mb-5 md:mb-6">
            <div className="relative w-full max-w-[700px] h-8">
              <svg viewBox="0 0 700 32" className="w-full h-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="var(--accent-300)" stopOpacity="0.5" />
                    <stop offset="50%" stopColor="var(--primary-300)" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="var(--accent-300)" stopOpacity="0.5" />
                  </linearGradient>
                </defs>
                <path
                  d="M0 4 Q 175 32 350 28 Q 525 24 700 4"
                  fill="none"
                  stroke="url(#flowGrad)"
                  strokeWidth="1.5"
                  strokeDasharray="6 4"
                />
                <triangle cx="690" cy="4" />
              </svg>
              <div className="absolute right-0 top-0">
                <i className="ri-arrow-down-s-line text-foreground-300/50" />
              </div>
            </div>
          </div>

          {/* 移动端中间箭头 */}
          <div className="flex md:hidden justify-center mb-5">
            <i className="ri-arrow-down-double-line text-foreground-300 text-lg" />
          </div>

          {/* 下排 3 个 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {trainingFlow.slice(3, 6).map((item, i) => {
              const isAccent = item.color === 'accent';
              return (
                <div key={item.step} className="relative">
                  <StepCard item={item} isAccent={isAccent} delay={660 + i * 120} />
                  {i < 2 && (
                    <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 items-center">
                      <div className="w-6 h-px bg-gradient-to-r from-primary-200/40 to-accent-200/40" />
                      <div className="w-6 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary-300/60" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 底部总结条 */}
        <div className={`mt-10 md:mt-14 max-w-3xl mx-auto transition-all duration-1000 delay-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative p-6 md:p-8 rounded-2xl border border-background-200/60 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(248,250,252,0.5) 100%)',
            }}
          >
            {/* 背景装饰 */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-300/40 via-accent-300/50 to-primary-300/40" />
            <svg className="absolute -right-4 -bottom-4 w-28 h-28 text-primary-200/10" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="8 4" />
            </svg>
            <svg className="absolute -left-3 -top-3 w-20 h-20 text-accent-200/10" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="30" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 4" />
            </svg>

            <div className="relative z-10 text-center">
              <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed">
                AI评课不是一开始就让模型自己判断。它的底层是
                <span className="inline-flex items-center gap-1 px-2 py-0.5 mx-1 rounded-md bg-primary-50 border border-primary-200/40 text-primary-700 font-semibold text-sm">
                  <i className="ri-user-voice-line text-xs" />
                  长期人工评价
                </span>
                <br className="hidden md:block" />
                先有专家按统一评价表评课
                <span className="inline-flex items-center mx-1 text-foreground-400">
                  <i className="ri-arrow-right-s-line text-sm" />
                </span>
                形成训练样本
                <span className="inline-flex items-center mx-1 text-foreground-400">
                  <i className="ri-arrow-right-s-line text-sm" />
                </span>
                模型学习
                <span className="inline-flex items-center mx-1 text-foreground-400">
                  <i className="ri-arrow-right-s-line text-sm" />
                </span>
                人机比对
                <span className="inline-flex items-center mx-1 text-foreground-400">
                  <i className="ri-arrow-right-s-line text-sm" />
                </span>
                系统应用
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepCard({
  item,
  isAccent,
  delay,
}: {
  item: typeof trainingFlow[0];
  isAccent: boolean;
  delay: number;
}) {
  return (
    <div
      className={`group relative rounded-2xl overflow-hidden transition-all duration-700 hover:-translate-y-2 ${
        isAccent
          ? 'card-premium-accent border-accent-200/50 hover:border-accent-300/70'
          : 'card-premium border-primary-200/50 hover:border-primary-300/70'
      }`}
      style={{
        background: isAccent
          ? 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,251,245,0.6) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(245,250,255,0.6) 100%)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {/* 顶部色条 */}
      <div className={`h-1 w-full ${isAccent ? 'bg-accent-400' : 'bg-primary-400'}`} />

      <div className="p-6 md:p-7 text-center">
        {/* 编号 + 图标圆 */}
        <div className="relative mx-auto w-fit mb-5">
          {/* 外圈装饰 */}
          <div className={`absolute -inset-3 rounded-full border-2 border-dashed opacity-20 ${isAccent ? 'border-accent-400' : 'border-primary-400'}`} />
          <div className={`relative w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-sm ${isAccent ? 'bg-accent-50' : 'bg-primary-50'}`}>
            <i className={`${item.icon} text-2xl ${isAccent ? 'text-accent-600' : 'text-primary-600'}`} />
          </div>
          <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm ${isAccent ? 'bg-accent-400' : 'bg-primary-400'}`}>
            {item.num}
          </div>
        </div>

        {/* 标题 */}
        <h4 className={`font-heading text-base md:text-lg font-bold mb-2 ${isAccent ? 'text-accent-700' : 'text-primary-700'}`}>
          {item.step}
        </h4>

        {/* 描述 */}
        <p className="text-xs md:text-sm text-foreground-500 font-body leading-relaxed">
          {item.desc}
        </p>

        {/* hover 发光装饰 */}
        <div className={`absolute -bottom-8 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${isAccent ? 'bg-accent-200' : 'bg-primary-200'}`} />
      </div>
    </div>
  );
}